import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { isNativeIOS, enableNativePush, disableNativePush, nativePushGranted } from "@/lib/nativePush";

const SW_URL = "/push-sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * usePushNotifications — encapsulates all the browser-side Web Push glue:
 *
 *   • Feature detection (Notification API + serviceWorker + PushManager)
 *   • Permission grant
 *   • Service-worker registration (once)
 *   • Subscribe/unsubscribe against the backend
 *   • Per-category preferences (message / like / gift / live_start)
 *
 * Returns everything the settings UI needs to render toggles + a "Send test"
 * button. Safe to call unconditionally — degrades gracefully when unsupported.
 */
export default function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState({
    message: true, like: true, gift: true, live_start: true,
  });

  // ------------------------------------------------------------------
  //  Support detection + existing-subscription check.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isNativeIOS()) {
      // Native iOS shell — APNs replaces web push entirely.
      setSupported(true);
      nativePushGranted().then((granted) => {
        setSubscribed(granted);
        if (granted) setPermission("granted");
      });
      return;
    }
    const ok =
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window;
    setSupported(ok);
    if (!ok) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_URL);
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch { /* silent */ }
    })();
  }, []);

  const registerSw = useCallback(async () => {
    if (!("serviceWorker" in navigator)) return null;
    // Idempotent — browsers dedupe by scope + script URL.
    return navigator.serviceWorker.register(SW_URL, { scope: "/" });
  }, []);

  // ------------------------------------------------------------------
  //  Enable / disable.
  // ------------------------------------------------------------------
  const enable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      if (isNativeIOS()) {
        const res = await enableNativePush();
        if (res.ok) { setSubscribed(true); setPermission("granted"); }
        return res;
      }
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "permission" };
      const reg = await registerSw();
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        prefs,
        user_agent: navigator.userAgent.slice(0, 300),
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: e?.message || "subscribe_failed" };
    } finally {
      setBusy(false);
    }
  }, [supported, busy, prefs, registerSw]);

  const disable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      if (isNativeIOS()) {
        await disableNativePush();
        setSubscribed(false);
        return { ok: true };
      }
      const reg = await navigator.serviceWorker.getRegistration(SW_URL);
      if (!reg) { setSubscribed(false); return { ok: true }; }
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await api.post("/push/unsubscribe", {
            endpoint: sub.endpoint,
            keys: sub.toJSON().keys || {},
          });
        } catch { /* server may be down — client-side unsub still happens */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      return { ok: true };
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  // ------------------------------------------------------------------
  //  Preferences — re-subscribe rewrites the row on the server.
  // ------------------------------------------------------------------
  const savePrefs = useCallback(async (next) => {
    setPrefs(next);
    if (!subscribed) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_URL);
      const sub = reg && (await reg.pushManager.getSubscription());
      if (!sub) return;
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        prefs: next,
        user_agent: navigator.userAgent.slice(0, 300),
      });
    } catch { /* leave prefs local — retry on toggle */ }
  }, [subscribed]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post("/push/test");
      return { ok: true, delivered: data.delivered };
    } catch (e) {
      return { ok: false, reason: e?.response?.data?.detail || "test_failed" };
    }
  }, []);

  return { supported, permission, subscribed, busy, prefs, setPrefs: savePrefs, enable, disable, sendTest };
}
